/**
 * TournamentRegistrationTab.jsx
 * RegistrationTab component — shows registered teams, rosters, and add-player dialog.
 */
import { useState, useEffect, useRef } from 'react';
import {
  Trophy, Users, Zap, Plus, Search, Trash2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { toast } from 'sonner';

const SPORT_LIST = [
  'table_tennis','badminton','chess','carrom','pickleball','squash','cricket','football',
].map(code => ({ code, name: code.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase()) }));

const ROLE_LABELS = { captain: 'C', vice_captain: 'VC', player: '' };

export function RegistrationTab({
  categories, orgTeams, registeredTeamIds,
  fixtureCount, onManageRegistration, onSetupTeams, orgSlug,
}) {
  const registeredTeams = orgTeams.filter(t => registeredTeamIds.has(t.id));
  const unregistered    = orgTeams.filter(t => !registeredTeamIds.has(t.id));

  const [rosters, setRosters]               = useState({});
  const [rosterLoading, setRosterLoading]   = useState(false);
  const [addPlayerOpen, setAddPlayerOpen]   = useState(false);
  const [addPlayerTarget, setAddPlayerTarget] = useState(null);
  const [playerSearch, setPlayerSearch]     = useState('');
  const [searchResults, setSearchResults]   = useState([]);
  const [searching, setSearching]           = useState(false);
  const [jerseyNo, setJerseyNo]             = useState('');
  const [playerRole, setPlayerRole]         = useState('player');
  const [playerSports, setPlayerSports]     = useState([]);
  const searchDebounce = useRef(null);

  useEffect(() => {
    if (registeredTeams.length > 0) fetchAllRosters();
  }, [registeredTeams.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAllRosters = async () => {
    setRosterLoading(true);
    try {
      const results = await Promise.allSettled(
        registeredTeams.map(t =>
          api.get(`/orgs/${orgSlug}/teams/${t.id}/roster`).then(r => ({
            teamId: t.id, players: Array.isArray(r.data) ? r.data : [],
          }))
        )
      );
      const map = {};
      results.forEach(r => { if (r.status === 'fulfilled') map[r.value.teamId] = r.value.players; });
      setRosters(map);
    } catch (_) {}
    finally { setRosterLoading(false); }
  };

  const handlePlayerSearch = (q) => {
    setPlayerSearch(q);
    clearTimeout(searchDebounce.current);
    if (!q.trim()) { setSearchResults([]); return; }
    searchDebounce.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`/orgs/${orgSlug}/players`, { params: { q: q.trim(), limit: 20 } });
        setSearchResults(Array.isArray(res.data) ? res.data : []);
      } catch (_) {}
      finally { setSearching(false); }
    }, 300);
  };

  const handleAddPlayer = async (player) => {
    if (!addPlayerTarget) return;
    try {
      await api.post(`/orgs/${orgSlug}/teams/${addPlayerTarget.id}/roster`, {
        player_id: player.id,
        ...(jerseyNo ? { jersey_number: parseInt(jerseyNo) } : {}),
        role_in_team: playerRole, sports: playerSports,
      });
      toast.success(`${player.full_name || player.first_name} added to ${addPlayerTarget.name}`);
      setPlayerSearch(''); setSearchResults([]); setJerseyNo(''); setPlayerSports([]);
      fetchAllRosters();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add player'); }
  };

  const handleRemovePlayer = async (teamId, pid, playerName) => {
    if (!confirm(`Remove ${playerName} from this team?`)) return;
    try {
      await api.delete(`/orgs/${orgSlug}/teams/${teamId}/roster/${pid}`);
      toast.success('Player removed'); fetchAllRosters();
    } catch (_) { toast.error('Failed to remove player'); }
  };

  return (
    <div className="space-y-6" data-testid="registration-tab">
      {/* Categories */}
      {categories.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-[#8a93a3] uppercase tracking-wider mb-3">Tournament Categories</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {categories.map(cat => (
              <Card key={cat.id} className="bg-[#161a26] border-[#252a37]">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 bg-[#1d2230] rounded-lg"><Trophy className="h-4 w-4 text-[#f5c518]" /></div>
                  <div className="min-w-0">
                    <p className="font-medium text-[#f0f2f7] text-sm truncate">{cat.name}</p>
                    <p className="text-xs text-[#8a93a3] capitalize">{cat.sport_code?.replace(/_/g,' ')} · {cat.participation} · {cat.gender}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Teams & Rosters */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[#8a93a3] uppercase tracking-wider">Teams & Rosters ({registeredTeams.length})</h3>
          <Button onClick={onManageRegistration} size="sm" className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]" data-testid="manage-registration-button">
            <Zap className="h-3.5 w-3.5 mr-1.5" />
            {fixtureCount > 0 ? 'Update Registration' : 'Register Teams & Generate Fixtures'}
          </Button>
        </div>

        {registeredTeams.length === 0 ? (
          <Card className="bg-[#161a26] border-[#252a37]">
            <CardContent className="p-8 text-center">
              <Users className="h-10 w-10 text-[#8a93a3] mx-auto mb-3" />
              <p className="text-[#f0f2f7] font-medium mb-1">No teams registered yet</p>
              <p className="text-sm text-[#8a93a3] mb-4">Set up new teams or pick from existing org teams</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={onSetupTeams} className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]" data-testid="setup-new-teams-button">
                  <Plus className="h-4 w-4 mr-2" /> Create New Teams
                </Button>
                <Button variant="outline" onClick={onManageRegistration} className="border-[#252a37] text-[#f0f2f7] hover:bg-[#1d2230]">
                  <Zap className="h-4 w-4 mr-2" /> Use Existing Teams
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {registeredTeams.map(team => {
              const roster = rosters[team.id] || [];
              return (
                <Card key={team.id} className="bg-[#161a26] border-[#252a37]">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-lg flex items-center justify-center font-bold text-sm flex-shrink-0 overflow-hidden"
                          style={{ backgroundColor: team.color || '#1d2230' }}>
                          {team.logo_url
                            ? <img src={team.logo_url} className="h-full w-full object-cover" alt={team.name} />
                            : <span className="text-white">{team.name?.[0]}</span>}
                        </div>
                        <div>
                          <p className="font-semibold text-[#f0f2f7]">{team.name}</p>
                          <p className="text-xs text-[#8a93a3]">{team.short_name} · {roster.length} player{roster.length!==1?'s':''}</p>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" data-testid="add-player-to-team-button"
                        onClick={() => { setAddPlayerTarget(team); setAddPlayerOpen(true); setPlayerSearch(''); setSearchResults([]); setJerseyNo(''); setPlayerRole('player'); setPlayerSports([]); }}
                        className="border-[#252a37] text-[#f5c518] hover:bg-[#1d2230] text-xs">
                        <Plus className="h-3.5 w-3.5 mr-1" /> Add Player
                      </Button>
                    </div>
                    {rosterLoading && !roster.length ? (
                      <div className="text-xs text-[#8a93a3] py-2">Loading roster…</div>
                    ) : roster.length > 0 ? (
                      <div className="space-y-1.5">
                        {roster.map(entry => {
                          const name = entry.player?.full_name || entry.player_name || `Player ${entry.player_id?.slice(-6)}`;
                          const role = ROLE_LABELS[entry.role_in_team] || '';
                          return (
                            <div key={entry.player_id||entry.id} className="flex items-center gap-2 px-2 py-1.5 bg-[#1d2230] rounded-lg">
                              <div className="h-6 w-6 rounded-full bg-[#252a37] flex items-center justify-center text-[#f5c518] font-medium text-xs flex-shrink-0">
                                {entry.jersey_number || name[0]}
                              </div>
                              <span className="flex-1 text-sm text-[#f0f2f7] truncate">{name}</span>
                              {role && <Badge className="bg-[rgba(245,197,24,0.12)] text-[#f5c518] border border-[rgba(245,197,24,0.3)] text-[10px] px-1.5">{role}</Badge>}
                              <button data-testid="remove-player-from-roster-button"
                                onClick={() => handleRemovePlayer(team.id, entry.player_id, name)}
                                className="text-[#8a93a3] hover:text-[#ef4444] transition-colors ml-1">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-[#8a93a3] py-1">No players assigned yet — click Add Player</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Unregistered teams */}
      {unregistered.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-[#8a93a3] uppercase tracking-wider mb-3">Available but Not Registered ({unregistered.length})</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {unregistered.map(team => (
              <div key={team.id} className="bg-[#161a26] border border-[#252a37] rounded-lg p-3 flex items-center gap-3 opacity-60">
                <div className="h-8 w-8 rounded-full bg-[#1d2230] flex items-center justify-center text-[#8a93a3] font-bold text-sm flex-shrink-0">{team.name?.[0]}</div>
                <p className="text-sm text-[#8a93a3] truncate">{team.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Player Dialog */}
      <Dialog open={addPlayerOpen} onOpenChange={setAddPlayerOpen}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-md" data-testid="add-player-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <Users className="h-5 w-5 text-[#f5c518]" />
              Add Player to {addPlayerTarget?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8a93a3]" />
              <Input value={playerSearch} onChange={e => handlePlayerSearch(e.target.value)}
                placeholder="Search players by name…" autoFocus
                className="pl-10 bg-[#1d2230] border-[#252a37] text-[#f0f2f7]"
                data-testid="roster-player-search-input" />
            </div>

            {searching && <p className="text-xs text-[#8a93a3] text-center py-2">Searching…</p>}
            {!searching && searchResults.length > 0 && (
              <ScrollArea className="max-h-[200px] bg-[#1d2230] rounded-lg border border-[#252a37]">
                {searchResults.map(p => (
                  <div key={p.id} onClick={() => handleAddPlayer(p)}
                    className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#252a37] border-b border-[#252a37]/50 last:border-0">
                    <div className="h-7 w-7 rounded-full bg-[#252a37] flex items-center justify-center text-[#f5c518] font-medium text-xs flex-shrink-0">
                      {(p.full_name||p.first_name)?.[0]?.toUpperCase()||'P'}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-[#f0f2f7] font-medium truncate">{p.full_name||`${p.first_name} ${p.last_name||''}`.trim()}</p>
                      {p.contact_phone && <p className="text-xs text-[#8a93a3] font-mono">{p.contact_phone}</p>}
                    </div>
                    <Plus className="h-4 w-4 text-[#f5c518] flex-shrink-0 ml-auto" />
                  </div>
                ))}
              </ScrollArea>
            )}
            {!searching && playerSearch && searchResults.length === 0 && (
              <p className="text-xs text-[#8a93a3] text-center py-3">No players found for "{playerSearch}"</p>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#8a93a3] text-xs mb-1 block">Jersey Number (optional)</Label>
                <Input type="number" min="1" max="99" value={jerseyNo} onChange={e => setJerseyNo(e.target.value)}
                  placeholder="e.g., 10" className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7]" />
              </div>
              <div>
                <Label className="text-[#8a93a3] text-xs mb-1 block">Role</Label>
                <Select value={playerRole} onValueChange={setPlayerRole}>
                  <SelectTrigger className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#161a26] border-[#252a37]">
                    <SelectItem value="player"       className="text-[#f0f2f7] focus:bg-[#1d2230]">Player</SelectItem>
                    <SelectItem value="captain"      className="text-[#f0f2f7] focus:bg-[#1d2230]">Captain</SelectItem>
                    <SelectItem value="vice_captain" className="text-[#f0f2f7] focus:bg-[#1d2230]">Vice Captain</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-[#8a93a3] text-xs mb-2 block">Sports played by this member in this team</Label>
              <div className="flex flex-wrap gap-2">
                {SPORT_LIST.map(sp => {
                  const isSelected = playerSports.includes(sp.code);
                  return (
                    <button key={sp.code} type="button"
                      onClick={() => setPlayerSports(prev => isSelected ? prev.filter(s=>s!==sp.code) : [...prev, sp.code])}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                        isSelected ? 'bg-[rgba(245,197,24,0.15)] border-[rgba(245,197,24,0.4)] text-[#f5c518]'
                                   : 'bg-[#1d2230] border-[#252a37] text-[#8a93a3] hover:border-[#8a93a3]'}`}>
                      {sp.name}
                    </button>
                  );
                })}
              </div>
              {playerSports.length > 0 && (
                <p className="text-xs text-[#f5c518] mt-1">{playerSports.length} sport{playerSports.length>1?'s':''} selected</p>
              )}
            </div>
            <p className="text-xs text-[#8a93a3]">Click a player to add them.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddPlayerOpen(false)} className="border-[#252a37] text-[#f0f2f7]">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
